import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import UserBadge from '../components/UserBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import LikeButton from '../components/LikeButton';
import { apiRequest, UPLOADS_BASE_URL } from '../config/api';
import { getAvatarStyle, getInitials } from '../utils/avatarUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Pilot {
  userid: string;
  name: string;
  organization?: string;
  logo?: string;
}

interface TimelineFlight {
  type: 'flight';
  _id: string;
  userid: string;
  aircraftid?: { _id: string; identification: string; type: string; image?: string } | null;
  dateStart: string;
  duration: number;
  region?: string;
  distanceKm: number;
  pilot: Pilot | null;
}

interface TimelineComment {
  type: 'comment';
  _id: string;
  content: string;
  createdAt: string;
  flightId: string;
  flight: {
    _id: string;
    aircraftid?: { _id: string; identification: string; type: string; image?: string } | null;
    region?: string;
    dateStart: string;
  };
  author: Pilot;
}

type TimelineActivity = TimelineFlight | TimelineComment;

interface Friend {
  userid: string;
  name: string;
  organization?: string;
  logo?: string;
  publicFlightCount: number;
}

interface FriendRequest {
  _id: string;
  requester: Pilot;
  createdAt: string;
}

interface DirectoryUser {
  userid: string;
  name: string;
  organization?: string;
  logo?: string;
  status: 'none' | 'pending_sent' | 'pending_received' | 'accepted';
  friendshipId?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const FLIGHT_ARTICLES: Record<string, string> = {
  balloon: 'eine Ballonfahrt', ballon: 'eine Ballonfahrt',
  glider: 'einen Segelflug',   segelflugzeug: 'einen Segelflug',
  plane: 'einen Motorflug',    motorflugzeug: 'einen Motorflug',
  helicopter: 'einen Hubschrauberflug', hubschrauber: 'einen Hubschrauberflug',
};

const AIRCRAFT_TYPE_LABELS: Record<string, string> = {
  balloon: 'Ballon',       ballon: 'Ballon',
  glider: 'Segelflugzeug', segelflugzeug: 'Segelflugzeug',
  plane: 'Motorflugzeug',  motorflugzeug: 'Motorflugzeug',
  helicopter: 'Hubschrauber', hubschrauber: 'Hubschrauber',
};

function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 2) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Minuten`;
  if (hours < 24) return `vor ${hours} Stunde${hours === 1 ? '' : 'n'}`;
  if (days < 30) return `vor ${days} Tag${days === 1 ? '' : 'en'}`;
  return new Date(dateStr).toLocaleDateString('de-DE');
}

function formatDuration(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}:${m.toString().padStart(2, '0')} h`;
}

function calculateDateRange(range: string): { from: string | null; to: string | null } {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  switch (range) {
    case 'Letzte 7 Tage': {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      return { from: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`, to: todayStr };
    }
    case 'Letzte 30 Tage': {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      return { from: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`, to: todayStr };
    }
    case 'Letzte 90 Tage': {
      const d = new Date(now); d.setDate(d.getDate() - 90);
      return { from: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`, to: todayStr };
    }
    case 'Dieses Jahr':
      return { from: `${now.getFullYear()}-01-01`, to: todayStr };
    default:
      return { from: null, to: null };
  }
}

function mapAircraftTypeToApi(label: string): string {
  const m: Record<string, string> = {
    'Alle Typen': 'all', 'Ballon': 'balloon', 'Segelflugzeug': 'glider',
    'Motorflugzeug': 'plane', 'Hubschrauber': 'helicopter',
  };
  return m[label] || 'all';
}

// ── Avatar helper ─────────────────────────────────────────────────────────────

const Avatar: React.FC<{ user: { name: string; logo?: string }; size?: number }> = ({ user, size = 40 }) => (
  user.logo
    ? <img src={`${UPLOADS_BASE_URL}${user.logo}`} alt={user.name}
        style={{ width: size, height: size, borderRadius: '6px', objectFit: 'contain', background: 'transparent', flexShrink: 0 }} />
    : <div style={getAvatarStyle(user.name, size)}>{getInitials(user.name)}</div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const Friends: React.FC = () => {
  const navigate = useNavigate();

  const [friends, setFriends] = useState<Friend[]>([]);
  const [timeline, setTimeline] = useState<TimelineActivity[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [flightLikes, setFlightLikes] = useState<Record<string, { count: number; likedByMe: boolean }>>({});

  // Filters
  const [friendFilter, setFriendFilter] = useState('all');
  const [aircraftType, setAircraftType] = useState('Alle Typen');
  const [dateRange, setDateRange] = useState('Letzte 30 Tage');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Add friend modal
  const [showModal, setShowModal] = useState(false);
  const [directory, setDirectory] = useState<DirectoryUser[]>([]);
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [requestSending, setRequestSending] = useState<string | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  const fetchFriends = useCallback(async () => {
    try {
      const [fRes, rRes] = await Promise.all([
        apiRequest('/friends'),
        apiRequest('/friends/requests'),
      ]);
      if (fRes.ok) setFriends(await fRes.json());
      if (rRes.ok) setRequests(await rRes.json());
    } catch {}
  }, []);

  const fetchTimeline = useCallback(async (options: {
    friendUserid: string; aircraftTypeApi: string; dateFrom: string | null; dateTo: string | null;
  }) => {
    setTimelineLoading(true);
    try {
      const params = new URLSearchParams();
      if (options.friendUserid !== 'all') params.append('friendUserid', options.friendUserid);
      if (options.aircraftTypeApi !== 'all') params.append('aircraftType', options.aircraftTypeApi);
      if (options.dateFrom) params.append('dateFrom', options.dateFrom);
      if (options.dateTo) params.append('dateTo', options.dateTo);
      const qs = params.toString();
      const res = await apiRequest(`/friends/timeline${qs ? `?${qs}` : ''}`);
      if (res.ok) {
        const flights: TimelineActivity[] = await res.json();
        setTimeline(flights);
        // Fetch like status for all flight entries in parallel
        const flightItems = flights.filter((f): f is TimelineFlight => f.type === 'flight');
        const likeEntries = await Promise.all(
          flightItems.map(async (f) => {
            try {
              const lr = await apiRequest(`/likes/flight/${f._id}`);
              if (lr.ok) return [f._id, await lr.json()] as const;
            } catch {}
            return [f._id, { count: 0, likedByMe: false }] as const;
          })
        );
        setFlightLikes(Object.fromEntries(likeEntries));
      }
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFriends();
    const dates = calculateDateRange('Letzte 30 Tage');
    fetchTimeline({ friendUserid: 'all', aircraftTypeApi: 'all', dateFrom: dates.from, dateTo: dates.to });
  }, [fetchFriends, fetchTimeline]);

  // ── Filter helpers ───────────────────────────────────────────────────────

  const currentDates = () =>
    dateRange.includes(' – ')
      ? { from: startDate || null, to: endDate || null }
      : calculateDateRange(dateRange);

  const applyFilters = (overrides?: { friendUserid?: string; aircraftTypeApi?: string; dateFrom?: string | null; dateTo?: string | null }) => {
    const dates = currentDates();
    fetchTimeline({
      friendUserid: overrides?.friendUserid ?? friendFilter,
      aircraftTypeApi: overrides?.aircraftTypeApi ?? mapAircraftTypeToApi(aircraftType),
      dateFrom: overrides?.dateFrom !== undefined ? overrides.dateFrom : dates.from,
      dateTo: overrides?.dateTo !== undefined ? overrides.dateTo : dates.to,
    });
  };

  const handleDateRangeSelect = (range: string) => {
    if (range === 'Benutzerdefiniert') { setShowCustomDate(true); return; }
    setShowCustomDate(false);
    setDateRange(range);
    const dates = calculateDateRange(range);
    fetchTimeline({ friendUserid: friendFilter, aircraftTypeApi: mapAircraftTypeToApi(aircraftType), dateFrom: dates.from, dateTo: dates.to });
  };

  const applyCustomDate = () => {
    if (!startDate || !endDate) return;
    setDateRange(`${startDate} – ${endDate}`);
    setShowCustomDate(false);
    fetchTimeline({ friendUserid: friendFilter, aircraftTypeApi: mapAircraftTypeToApi(aircraftType), dateFrom: startDate, dateTo: endDate });
  };

  // ── Friend requests ──────────────────────────────────────────────────────

  const handleRequest = async (requestId: string, action: 'accept' | 'decline') => {
    try {
      await apiRequest(`/friends/requests/${requestId}/${action}`, { method: 'PUT' });
      await fetchFriends();
      applyFilters();
    } catch {}
  };

  // ── Directory / Add friend ───────────────────────────────────────────────

  const openDirectory = async () => {
    setShowModal(true);
    setDirectoryLoading(true);
    try {
      const res = await apiRequest('/friends/directory');
      if (res.ok) setDirectory(await res.json());
    } finally {
      setDirectoryLoading(false);
    }
  };

  const sendRequest = async (targetUserid: string) => {
    setRequestSending(targetUserid);
    try {
      await apiRequest(`/friends/request/${targetUserid}`, { method: 'POST' });
      setDirectory(prev => prev.map(u =>
        u.userid === targetUserid ? { ...u, status: 'pending_sent' } : u
      ));
    } finally {
      setRequestSending(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const friendOptions = [{ userid: 'all', name: 'Alle Freunde' }, ...friends];
  const filteredDirectory = directory.filter(u =>
    u.name.toLowerCase().includes(directorySearch.toLowerCase()) ||
    (u.organization || '').toLowerCase().includes(directorySearch.toLowerCase())
  );

  return (
    <div className="container" style={{ padding: '40px' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 style={{ color: '#687088', fontWeight: 500, margin: 0 }}>Aktivitäten meiner Freunde</h3>
        <UserBadge />
      </div>

      {/* Filters */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
        <div className="card-body p-4">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            {/* Friend filter */}
            <span style={{ color: '#687088', fontSize: '14px' }}>Freunde</span>
            <div className="dropdown">
              <button className="btn btn-primary dropdown-toggle" style={{ fontWeight: 700, fontSize: '13px' }}
                type="button" data-bs-toggle="dropdown" aria-expanded="false">
                {friendOptions.find(f => f.userid === friendFilter)?.name || 'Alle Freunde'}
              </button>
              <ul className="dropdown-menu">
                {friendOptions.map(f => (
                  <li key={f.userid}>
                    <a className={`dropdown-item ${friendFilter === f.userid ? 'active' : ''}`} href="#"
                      onClick={(e) => { e.preventDefault(); setFriendFilter(f.userid); applyFilters({ friendUserid: f.userid }); }}>
                      {f.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Aircraft type filter */}
            <span style={{ color: '#687088', fontSize: '14px' }}>Flugobjekt</span>
            <div className="dropdown">
              <button className="btn btn-outline-secondary dropdown-toggle"
                style={{ color: '#687088', borderColor: '#dee2e6', fontSize: '13px' }}
                type="button" data-bs-toggle="dropdown" aria-expanded="false">
                {aircraftType}
              </button>
              <ul className="dropdown-menu">
                {['Alle Typen', 'Ballon', 'Segelflugzeug', 'Motorflugzeug', 'Hubschrauber'].map(t => (
                  <li key={t}>
                    <a className={`dropdown-item ${aircraftType === t ? 'active' : ''}`} href="#"
                      onClick={(e) => { e.preventDefault(); setAircraftType(t); applyFilters({ aircraftTypeApi: mapAircraftTypeToApi(t) }); }}>
                      {t}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Date range filter */}
            <span style={{ color: '#687088', fontSize: '14px' }}>Zeitraum</span>
            <div className="dropdown">
              <button className="btn btn-outline-secondary dropdown-toggle"
                style={{ color: '#687088', borderColor: '#dee2e6', fontSize: '13px' }}
                type="button" data-bs-toggle="dropdown" aria-expanded="false">
                {dateRange}
              </button>
              <ul className="dropdown-menu"
                style={{ minWidth: showCustomDate ? '320px' : 'auto', padding: showCustomDate ? '16px' : '' }}
                onClick={(e) => { if (showCustomDate) e.stopPropagation(); }}>
                {!showCustomDate ? (
                  <>
                    {['Letzte 7 Tage', 'Letzte 30 Tage', 'Letzte 90 Tage', 'Dieses Jahr'].map(r => (
                      <li key={r}><a className={`dropdown-item ${dateRange === r ? 'active' : ''}`} href="#"
                        onClick={(e) => { e.preventDefault(); handleDateRangeSelect(r); }}>{r}</a></li>
                    ))}
                    <li><hr className="dropdown-divider" /></li>
                    <li><a className="dropdown-item" href="#"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDateRangeSelect('Benutzerdefiniert'); }}>Benutzerdefiniert...</a></li>
                  </>
                ) : (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontSize: '13px', color: '#687088', fontWeight: 600 }}>Von</label>
                      <input type="date" className="form-control" value={startDate}
                        onChange={(e) => setStartDate(e.target.value)} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontSize: '13px', color: '#687088', fontWeight: 600 }}>Bis</label>
                      <input type="date" className="form-control" value={endDate}
                        onChange={(e) => setEndDate(e.target.value)} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '13px', flex: 1 }}
                        onClick={() => { setShowCustomDate(false); setStartDate(''); setEndDate(''); }}>Abbrechen</button>
                      <button className="btn btn-sm btn-primary" style={{ fontSize: '13px', fontWeight: 700, flex: 1 }}
                        onClick={applyCustomDate} disabled={!startDate || !endDate}>Anwenden</button>
                    </div>
                  </div>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main content: timeline + friends panel */}
      <div className="row g-4">
        {/* Left: pending requests + timeline */}
        <div className="col-lg-8">
          {/* Pending requests */}
          {requests.length > 0 && (
            <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
              <div className="card-body p-4">
                <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>
                  Freundschaftsanfragen ({requests.length})
                </h5>
                {requests.map(req => (
                  <div key={req._id} className="d-flex align-items-center justify-content-between py-2"
                    style={{ borderBottom: '1px solid #f0f2f7' }}>
                    <div className="d-flex align-items-center gap-3">
                      <Avatar user={req.requester} size={40} />
                      <div>
                        <div
                          style={{ fontWeight: 700, color: '#0d6efd', fontSize: '14px', cursor: 'pointer' }}
                          onClick={() => navigate(`/pilots/${req.requester.userid}`)}
                        >
                          {req.requester.name}
                        </div>
                        {req.requester.organization && (
                          <div style={{ color: '#a0afc0', fontSize: '12px' }}>{req.requester.organization}</div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-primary" style={{ fontWeight: 700, fontSize: '12px' }}
                        onClick={() => handleRequest(req._id, 'accept')}>Annehmen</button>
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '12px' }}
                        onClick={() => handleRequest(req._id, 'decline')}>Ablehnen</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Timeline</h5>
              {timelineLoading ? (
                <LoadingSpinner message="Lade Aktivitäten..." />
              ) : timeline.length === 0 ? (
                <p style={{ color: '#a0afc0', fontSize: '14px', margin: 0 }}>
                  Keine Aktivitäten im gewählten Zeitraum.
                </p>
              ) : (
                timeline.map((activity, i) => {
                  const divider = {
                    paddingTop: i > 0 ? '16px' : 0,
                    marginTop: i > 0 ? '16px' : 0,
                    borderTop: i > 0 ? '1px solid #f0f2f7' : 'none',
                  };

                  if (activity.type === 'comment') {
                    const ac = activity.flight?.aircraftid;
                    const identification = ac?.identification || '–';
                    return (
                      <div key={`c-${activity._id}`} style={divider}>
                        <div className="d-flex gap-3">
                          <div style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => navigate(`/pilots/${activity.author.userid}`)}>
                            <Avatar user={activity.author} size={44} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div>
                              <span
                                style={{ fontWeight: 700, color: '#0d6efd', cursor: 'pointer', fontSize: '14px' }}
                                onClick={() => navigate(`/pilots/${activity.author.userid}`)}
                              >
                                {activity.author.name}
                              </span>
                              <span style={{ color: '#687088', fontSize: '14px' }}>
                                {' '}hat kommentiert: {identification}
                              </span>
                            </div>
                            <div style={{
                              background: '#f4f6f9', borderRadius: '8px', padding: '6px 10px',
                              marginTop: '4px', fontSize: '13px', color: '#3d4554',
                              maxWidth: '400px', wordBreak: 'break-word',
                            }}>
                              {activity.content.length > 120
                                ? activity.content.slice(0, 120) + '…'
                                : activity.content}
                            </div>
                            <div style={{ color: '#a0afc0', fontSize: '12px', marginTop: '4px' }}>
                              {activity.flight?.region && `${activity.flight.region} · `}
                              {formatRelativeTime(activity.createdAt)}
                            </div>
                            <button
                              className="btn btn-primary btn-sm mt-2"
                              style={{ fontWeight: 700, fontSize: '12px' }}
                              onClick={() => navigate(`/all-flights/${activity.flightId}`)}
                            >
                              Zum Flug
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // type === 'flight'
                  const flight = activity;
                  const acType = flight.aircraftid?.type?.toLowerCase() || '';
                  const article = FLIGHT_ARTICLES[acType] || 'einen Flug';
                  const identification = flight.aircraftid?.identification || '–';
                  const imageUrl = flight.aircraftid?.image ? `${UPLOADS_BASE_URL}${flight.aircraftid.image}` : null;
                  return (
                    <div key={`f-${flight._id}`} style={divider}>
                      <div className="d-flex gap-3">
                        {flight.pilot && (
                          <div style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => navigate(`/pilots/${flight.pilot!.userid}`)}>
                            <Avatar user={flight.pilot} size={44} />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div>
                            <span
                              style={{ fontWeight: 700, color: '#0d6efd', cursor: 'pointer', fontSize: '14px' }}
                              onClick={() => flight.pilot && navigate(`/pilots/${flight.pilot.userid}`)}
                            >
                              {flight.pilot?.name || '–'}
                            </span>
                            <span style={{ color: '#687088', fontSize: '14px' }}>
                              {' '}hat {article} geteilt: {identification}
                              {flight.duration > 0 && ` · ${formatDuration(flight.duration)}`}
                              {flight.distanceKm > 0 && ` · ${flight.distanceKm} km`}
                            </span>
                          </div>
                          <div style={{ color: '#a0afc0', fontSize: '12px', marginTop: '2px' }}>
                            {flight.region && `${flight.region} · `}{formatRelativeTime(flight.dateStart)}
                          </div>
                          <div className="d-flex align-items-center gap-2 mt-2">
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ fontWeight: 700, fontSize: '12px' }}
                              onClick={() => navigate(`/all-flights/${flight._id}`)}
                            >
                              Details
                            </button>
                            <LikeButton
                              targetType="flight"
                              targetId={flight._id}
                              initialCount={flightLikes[flight._id]?.count ?? 0}
                              initialLiked={flightLikes[flight._id]?.likedByMe ?? false}
                            />
                          </div>
                        </div>
                        {imageUrl && (
                          <img src={imageUrl} alt={identification}
                            style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right: Friends panel */}
        <div className="col-lg-4">
          <div style={{
            background: 'linear-gradient(160deg, #0d6efd 0%, #0a58ca 100%)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(13,110,253,0.3)',
          }}>
            <h5 style={{ fontWeight: 700, color: 'white', marginBottom: '20px' }}>Freunde</h5>
            {friends.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Noch keine Freunde hinzugefügt.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {friends.map(friend => (
                  <div key={friend.userid} className="d-flex align-items-center gap-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/pilots/${friend.userid}`)}>
                    <Avatar user={friend} size={40} />
                    <div>
                      <div style={{ fontWeight: 700, color: 'white', fontSize: '14px' }}>{friend.name}</div>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                        {friend.organization
                          ? friend.organization
                          : `${friend.publicFlightCount} Flüge`}
                        {friend.organization && ` · ${friend.publicFlightCount} Flüge`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              className="btn mt-4 w-100"
              style={{ background: 'white', color: '#0d6efd', fontWeight: 700, fontSize: '13px' }}
              onClick={openDirectory}
            >
              Hinzufügen
            </button>
          </div>
        </div>
      </div>

      {/* Add Friend Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #e0e4ef' }}>
                <h5 className="modal-title" style={{ fontWeight: 700, color: '#687088' }}>Nutzer finden</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body" style={{ padding: '20px' }}>
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Name oder Organisation suchen..."
                  value={directorySearch}
                  onChange={(e) => setDirectorySearch(e.target.value)}
                  style={{ fontSize: '14px' }}
                />
                {directoryLoading ? (
                  <LoadingSpinner message="Lade Nutzer..." />
                ) : filteredDirectory.length === 0 ? (
                  <p style={{ color: '#a0afc0', fontSize: '14px' }}>Keine Nutzer gefunden.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filteredDirectory.map(u => (
                      <div key={u.userid} className="d-flex align-items-center justify-content-between"
                        style={{ padding: '8px 0', borderBottom: '1px solid #f0f2f7' }}>
                        <div className="d-flex align-items-center gap-3"
                          style={{ cursor: 'pointer' }}
                          onClick={() => { setShowModal(false); navigate(`/pilots/${u.userid}`); }}>
                          <Avatar user={u} size={40} />
                          <div>
                            <div style={{ fontWeight: 700, color: '#687088', fontSize: '14px' }}>{u.name}</div>
                            {u.organization && (
                              <div style={{ color: '#a0afc0', fontSize: '12px' }}>{u.organization}</div>
                            )}
                          </div>
                        </div>
                        <div>
                          {u.status === 'none' && (
                            <button className="btn btn-sm btn-primary" style={{ fontWeight: 700, fontSize: '12px' }}
                              disabled={requestSending === u.userid}
                              onClick={() => sendRequest(u.userid)}>
                              {requestSending === u.userid ? '...' : 'Anfrage senden'}
                            </button>
                          )}
                          {u.status === 'pending_sent' && (
                            <span style={{ color: '#a0afc0', fontSize: '12px' }}>Anfrage gesendet</span>
                          )}
                          {u.status === 'pending_received' && (
                            <span style={{ color: '#0d6efd', fontSize: '12px', fontWeight: 600 }}>Anfrage erhalten</span>
                          )}
                          {u.status === 'accepted' && (
                            <span style={{ color: '#198754', fontSize: '12px', fontWeight: 600 }}>Befreundet</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Friends;
